import type { Timestamp } from "@firebase/firestore-types";
import { isTreezorUserPep } from "./helpers.js";
import {
  DocumentTypeId,
  PreReviewDocumentStatus,
  RelationshipTypeId,
  type Kyc,
  type PreReviewDocument,
  type Relationship,
  type TaxResidence,
  type User,
  type UserWithKyc,
} from "./models.js";

export const TREEZOR_KYC_REQUIRED_DOCUMENT_TYPE_IDS = [
  DocumentTypeId.LegalStatus,
] as const satisfies ReadonlyArray<DocumentTypeId>;

export const TREEZOR_KYC_REQUIRED_COMPANY_FIELDS = [
  "address1",
  "postcode",
  "city",
  "country",
  "phone",
  "legalName",
  "legalRegistrationNumber",
  "legalForm",
  "legalRegistrationDate",
  "legalAnnualTurnOver",
  "legalNetIncomeRange",
  "legalNumberOfEmployeeRange",
  "legalSector",
  "legalShareCapital",
  "entitySanctionsQuestionnaire",
  "activityOutsideEu",
] as const satisfies ReadonlyArray<keyof User>;

export const TREEZOR_KYC_SANCTIONS_DETAIL_FIELDS = [
  "economicSanctions",
  "residentCountriesSanctions",
  "involvedSanctions",
] as const satisfies ReadonlyArray<keyof User>;

export type TreezorKycCompanyField =
  | (typeof TREEZOR_KYC_REQUIRED_COMPANY_FIELDS)[number]
  | (typeof TREEZOR_KYC_SANCTIONS_DETAIL_FIELDS)[number];

const isTreezorKycCompanyFieldFilled = (company: User, field: TreezorKycCompanyField) => {
  const value = company[field];
  switch (field) {
    case "entitySanctionsQuestionnaire":
      return value === 1 || value === 2 || value === 3 || value === 4;
    case "activityOutsideEu":
    case "economicSanctions":
    case "residentCountriesSanctions":
    case "involvedSanctions":
      return value === 0 || value === 1;
    case "address1":
    case "postcode":
    case "city":
    case "country":
    case "phone":
    case "legalName":
    case "legalRegistrationNumber":
    case "legalForm":
    case "legalRegistrationDate":
    case "legalAnnualTurnOver":
    case "legalNetIncomeRange":
    case "legalNumberOfEmployeeRange":
    case "legalSector":
    case "legalShareCapital":
      return !!value;
  }
};

export const TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS = [
  "specifiedUSPerson",
  "firstname",
  "lastname",
  "birthday",
  "placeOfBirth",
  "birthCountry",
  "nationality",
  "phone",
  "address1",
  "postcode",
  "city",
  "country",
] as const satisfies ReadonlyArray<keyof User>;

export type TreezorKycEffectiveBeneficiaryField =
  | (typeof TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS)[number]
  | (typeof TREEZOR_KYC_PEP_EFFECTIVE_BENEFICIARY_FIELDS)[number];

export const TREEZOR_KYC_REQUIRED_RELATIONSHIP_TYPE_IDS = [
  RelationshipTypeId.LegalRepresentative,
  RelationshipTypeId.EffectiveBeneficiaryShares,
] as const satisfies ReadonlyArray<Relationship["relationshipTypeId"]>;

export const TREEZOR_KYC_IDENTITY_DOCUMENT_TYPE_IDS = [
  DocumentTypeId.IdentityCard,
  DocumentTypeId.ResidencePermit,
  DocumentTypeId.Passport,
] as const satisfies ReadonlyArray<DocumentTypeId>;

export const TREEZOR_KYC_W9_DOCUMENT_TYPE_ID = DocumentTypeId.W9Form;

export const isTreezorEffectiveBeneficiaryUsResident = ({
  country,
  specifiedUSPerson,
  taxResidences,
}: {
  country?: string | null;
  specifiedUSPerson?: User["specifiedUSPerson"] | boolean | null;
  taxResidences?: ReadonlyArray<Pick<TaxResidence, "country" | "isDeleted">>;
}) =>
  country === "US" ||
  specifiedUSPerson === 1 ||
  specifiedUSPerson === true ||
  (taxResidences ?? []).some(
    (taxResidence) => !taxResidence.isDeleted && taxResidence.country === "US",
  );

export const TREEZOR_KYC_EFFECTIVE_BENEFICIARY_DOCUMENT_REQUIREMENTS = {
  [RelationshipTypeId.LegalRepresentative]: [
    { id: "identity", anyOf: TREEZOR_KYC_IDENTITY_DOCUMENT_TYPE_IDS },
  ],
  [RelationshipTypeId.EffectiveBeneficiaryShares]: [
    { id: "identity", anyOf: TREEZOR_KYC_IDENTITY_DOCUMENT_TYPE_IDS },
  ],
  [RelationshipTypeId.CompanyRepresentative]: [],
  [RelationshipTypeId.Employee]: [],
} as const satisfies Record<
  Relationship["relationshipTypeId"],
  ReadonlyArray<{ readonly id: string; readonly anyOf: ReadonlyArray<DocumentTypeId> }>
>;

export const TREEZOR_KYC_W9_DOCUMENT_REQUIREMENT = {
  id: "w9",
  anyOf: [TREEZOR_KYC_W9_DOCUMENT_TYPE_ID],
} as const;

export const TREEZOR_KYC_PEP_DOCUMENT_REQUIREMENTS = [
  { id: "pepAssets", anyOf: [DocumentTypeId.PepAssetsDeclaration] },
  { id: "pepIncome", anyOf: [DocumentTypeId.NaturalPersonRevenueAssetsDeclaration] },
  { id: "pepAddress", anyOf: [DocumentTypeId.ProofOfAddress] },
] as const satisfies ReadonlyArray<{
  readonly id: string;
  readonly anyOf: ReadonlyArray<DocumentTypeId>;
}>;

export const TREEZOR_KYC_DELEGATED_COMPANY_REPRESENTATIVE_DOCUMENT_REQUIREMENTS = [
  { id: "legalRepresentativePowerOfAttorney", anyOf: [DocumentTypeId.PowerOfAttorney] },
  { id: "legalRepresentativeProofOfPower", anyOf: [DocumentTypeId.Kbis] },
] as const satisfies ReadonlyArray<{
  readonly id: string;
  readonly anyOf: ReadonlyArray<DocumentTypeId>;
}>;

export const TREEZOR_KYC_PEP_EFFECTIVE_BENEFICIARY_FIELDS = [
  "occupationCategory",
  "monthlyIncomeRange",
  "personalAssetsRange",
] as const satisfies ReadonlyArray<keyof User>;

export type TreezorKycEffectiveBeneficiaryDocumentRequirement =
  | (typeof TREEZOR_KYC_EFFECTIVE_BENEFICIARY_DOCUMENT_REQUIREMENTS)[Relationship["relationshipTypeId"]][number]
  | typeof TREEZOR_KYC_W9_DOCUMENT_REQUIREMENT
  | (typeof TREEZOR_KYC_PEP_DOCUMENT_REQUIREMENTS)[number]
  | (typeof TREEZOR_KYC_DELEGATED_COMPANY_REPRESENTATIVE_DOCUMENT_REQUIREMENTS)[number];

export const getTreezorKycEffectiveBeneficiaryDocumentRequirements = (effectiveBeneficiary: {
  relationshipTypeIds: ReadonlyArray<Relationship["relationshipTypeId"]>;
  user?: {
    country?: string | null;
    specifiedUSPerson?: User["specifiedUSPerson"] | boolean | null;
    userTag?: User["userTag"];
  };
  taxResidences?: ReadonlyArray<Pick<TaxResidence, "country" | "isDeleted">>;
  isAccountCreator: boolean;
}) => {
  const requirementsById = new Map<
    TreezorKycEffectiveBeneficiaryDocumentRequirement["id"],
    TreezorKycEffectiveBeneficiaryDocumentRequirement
  >();
  for (const relationshipTypeId of effectiveBeneficiary.relationshipTypeIds) {
    for (const requirement of TREEZOR_KYC_EFFECTIVE_BENEFICIARY_DOCUMENT_REQUIREMENTS[
      relationshipTypeId
    ]) {
      if (effectiveBeneficiary.isAccountCreator && requirement.id === "identity") continue;
      requirementsById.set(requirement.id, requirement);
    }
  }
  if (
    effectiveBeneficiary.relationshipTypeIds.includes(
      RelationshipTypeId.EffectiveBeneficiaryShares,
    ) &&
    isTreezorEffectiveBeneficiaryUsResident({
      country: effectiveBeneficiary.user?.country,
      specifiedUSPerson: effectiveBeneficiary.user?.specifiedUSPerson,
      taxResidences: effectiveBeneficiary.taxResidences,
    })
  ) {
    requirementsById.set(
      TREEZOR_KYC_W9_DOCUMENT_REQUIREMENT.id,
      TREEZOR_KYC_W9_DOCUMENT_REQUIREMENT,
    );
  }
  if (
    effectiveBeneficiary.relationshipTypeIds.includes(
      RelationshipTypeId.EffectiveBeneficiaryShares,
    ) &&
    isTreezorUserPep(effectiveBeneficiary.user?.userTag)
  ) {
    for (const requirement of TREEZOR_KYC_PEP_DOCUMENT_REQUIREMENTS)
      requirementsById.set(requirement.id, requirement);
  }
  if (
    effectiveBeneficiary.relationshipTypeIds.includes(RelationshipTypeId.CompanyRepresentative) &&
    !effectiveBeneficiary.relationshipTypeIds.includes(RelationshipTypeId.LegalRepresentative)
  ) {
    for (const requirement of TREEZOR_KYC_DELEGATED_COMPANY_REPRESENTATIVE_DOCUMENT_REQUIREMENTS)
      requirementsById.set(requirement.id, requirement);
  }
  return [...requirementsById.values()];
};

const TREEZOR_KYC_REVIEW_ALREADY_SUBMITTED = new Set<Kyc["kycStatus"]>(["PENDING", "VALIDATED"]);

export const getTreezorKycReviewReadiness = ({
  company,
  documents,
  effectiveBeneficiaries,
  accountCreatorUserId,
  accountCreatorHasRoleRelationship,
  accountCreatorHasKycLiveness,
  requireValidatedDocuments = false,
}: {
  company: UserWithKyc | undefined;
  documents: ReadonlyArray<Pick<PreReviewDocument, "documentType" | "status">>;
  accountCreatorUserId: number | undefined;
  accountCreatorHasRoleRelationship: boolean;
  accountCreatorHasKycLiveness: boolean;
  effectiveBeneficiaries: ReadonlyArray<{
    userId: number;
    relationships: ReadonlyArray<Pick<Relationship, "relationshipTypeId" | "value">>;
    user?: Pick<User, TreezorKycEffectiveBeneficiaryField | "userTag">;
    documents?: ReadonlyArray<Pick<PreReviewDocument, "documentType" | "status">>;
    taxResidences?: ReadonlyArray<Pick<TaxResidence, "country" | "isDeleted" | "taxPayerId">>;
  }>;
  requireValidatedDocuments?: boolean;
}) => {
  const missingCompanyFields: TreezorKycCompanyField[] = company
    ? TREEZOR_KYC_REQUIRED_COMPANY_FIELDS.filter(
        (field) => !isTreezorKycCompanyFieldFilled(company, field),
      )
    : [...TREEZOR_KYC_REQUIRED_COMPANY_FIELDS];
  if (company?.activityOutsideEu === 1) {
    missingCompanyFields.push(
      ...TREEZOR_KYC_SANCTIONS_DETAIL_FIELDS.filter(
        (field) => !isTreezorKycCompanyFieldFilled(company, field),
      ),
    );
  }
  const presentDocumentTypeIds = new Set(
    documents
      .filter((document) =>
        requireValidatedDocuments
          ? document.status === PreReviewDocumentStatus.Validated
          : document.status === PreReviewDocumentStatus.Pending ||
            document.status === PreReviewDocumentStatus.Validated,
      )
      .map((document) => document.documentType),
  );
  const missingDocumentTypeIds = TREEZOR_KYC_REQUIRED_DOCUMENT_TYPE_IDS.filter(
    (documentTypeId) => !presentDocumentTypeIds.has(documentTypeId),
  );
  const presentRelationshipTypeIds = new Set(
    effectiveBeneficiaries.flatMap((effectiveBeneficiary) =>
      effectiveBeneficiary.relationships.map((relationship) => relationship.relationshipTypeId),
    ),
  );
  const missingRelationshipTypeIds = TREEZOR_KYC_REQUIRED_RELATIONSHIP_TYPE_IDS.filter(
    (relationshipTypeId) => !presentRelationshipTypeIds.has(relationshipTypeId),
  );
  const incompleteEffectiveBeneficiaries: Array<{
    userId: number;
    relationshipTypeIds: Array<Relationship["relationshipTypeId"]>;
    missingFields: TreezorKycEffectiveBeneficiaryField[];
    missingDocumentRequirements: TreezorKycEffectiveBeneficiaryDocumentRequirement[];
    missingTaxResidence: boolean;
    missingSharesPercent: boolean;
  }> = [];
  let effectiveBeneficiaryCheckTotal = 0;
  let effectiveBeneficiaryMissingCount = 0;
  for (const effectiveBeneficiary of effectiveBeneficiaries) {
    const relationshipTypeIds = effectiveBeneficiary.relationships.map(
      (relationship) => relationship.relationshipTypeId,
    );
    const isAccountCreator = effectiveBeneficiary.userId === accountCreatorUserId;
    const isUbo = relationshipTypeIds.includes(RelationshipTypeId.EffectiveBeneficiaryShares);
    const isPep = isUbo && isTreezorUserPep(effectiveBeneficiary.user?.userTag);
    const requiredFields: TreezorKycEffectiveBeneficiaryField[] = [
      ...(isAccountCreator || isUbo ? TREEZOR_KYC_REQUIRED_EFFECTIVE_BENEFICIARY_FIELDS : []),
      ...(isPep ? TREEZOR_KYC_PEP_EFFECTIVE_BENEFICIARY_FIELDS : []),
    ];
    const missingFields = requiredFields.filter(
      (field) =>
        effectiveBeneficiary.user?.[field] === undefined ||
        effectiveBeneficiary.user?.[field] === null ||
        effectiveBeneficiary.user?.[field] === "",
    );
    const acceptedDocumentTypeIds = new Set(
      (effectiveBeneficiary.documents ?? [])
        .filter((document) =>
          requireValidatedDocuments
            ? document.status === PreReviewDocumentStatus.Validated
            : document.status === PreReviewDocumentStatus.Pending ||
              document.status === PreReviewDocumentStatus.Validated,
        )
        .map((document) => document.documentType),
    );
    const documentRequirements = getTreezorKycEffectiveBeneficiaryDocumentRequirements({
      relationshipTypeIds,
      user: effectiveBeneficiary.user,
      taxResidences: effectiveBeneficiary.taxResidences,
      isAccountCreator,
    });
    const missingDocumentRequirements = documentRequirements.filter(
      (requirement) =>
        !requirement.anyOf.some((documentTypeId) => acceptedDocumentTypeIds.has(documentTypeId)),
    );
    const taxResidenceRequired = relationshipTypeIds.includes(
      RelationshipTypeId.EffectiveBeneficiaryShares,
    );
    const missingTaxResidence =
      taxResidenceRequired &&
      !(effectiveBeneficiary.taxResidences ?? []).some(
        (taxResidence) =>
          !taxResidence.isDeleted &&
          !!taxResidence.country &&
          (taxResidence.country === "FR" || !!taxResidence.taxPayerId),
      );
    const sharesRelationship = effectiveBeneficiary.relationships.find(
      (relationship) =>
        relationship.relationshipTypeId === RelationshipTypeId.EffectiveBeneficiaryShares,
    );
    const missingSharesPercent = !!sharesRelationship && !sharesRelationship.value;
    effectiveBeneficiaryCheckTotal +=
      requiredFields.length +
      documentRequirements.length +
      (taxResidenceRequired ? 1 : 0) +
      (sharesRelationship ? 1 : 0);
    if (
      missingFields.length === 0 &&
      missingDocumentRequirements.length === 0 &&
      !missingTaxResidence &&
      !missingSharesPercent
    )
      continue;
    incompleteEffectiveBeneficiaries.push({
      userId: effectiveBeneficiary.userId,
      relationshipTypeIds,
      missingFields,
      missingDocumentRequirements,
      missingTaxResidence,
      missingSharesPercent,
    });
    effectiveBeneficiaryMissingCount +=
      missingFields.length +
      missingDocumentRequirements.length +
      (missingTaxResidence ? 1 : 0) +
      (missingSharesPercent ? 1 : 0);
  }
  const alreadySubmitted =
    !!company?.kyc && TREEZOR_KYC_REVIEW_ALREADY_SUBMITTED.has(company.kyc.kycStatus);
  const checkTotal =
    TREEZOR_KYC_REQUIRED_COMPANY_FIELDS.length +
    (company?.activityOutsideEu === 1 ? TREEZOR_KYC_SANCTIONS_DETAIL_FIELDS.length : 0) +
    TREEZOR_KYC_REQUIRED_DOCUMENT_TYPE_IDS.length +
    TREEZOR_KYC_REQUIRED_RELATIONSHIP_TYPE_IDS.length +
    2 +
    effectiveBeneficiaryCheckTotal;
  const completedCheckCount =
    checkTotal -
    missingCompanyFields.length -
    missingDocumentTypeIds.length -
    missingRelationshipTypeIds.length -
    (accountCreatorHasRoleRelationship ? 0 : 1) -
    (accountCreatorHasKycLiveness ? 0 : 1) -
    effectiveBeneficiaryMissingCount;
  const dataComplete = completedCheckCount === checkTotal;
  return {
    missingCompanyFields,
    missingDocumentTypeIds,
    missingRelationshipTypeIds,
    missingAccountCreatorRelationship: !accountCreatorHasRoleRelationship,
    missingAccountCreatorKycLiveness: !accountCreatorHasKycLiveness,
    incompleteEffectiveBeneficiaries,
    alreadySubmitted,
    dataComplete,
    checkTotal,
    completedCheckCount,
  };
};

export type TreezorKycLiveness = {
  identificationId: string;
  startedAt: Timestamp;
  result?: {
    score: number;
    kycStatus: string;
    comment?: string;
    reasonCodes: string[];
    receivedAt: Timestamp;
  };
};

export const TREEZOR_KYC_LIVENESS_STATUSES = [
  "none",
  "started",
  "verified",
  "refused",
  "failed",
] as const;
export type TreezorKycLivenessStatus = (typeof TREEZOR_KYC_LIVENESS_STATUSES)[number];

// Treezor flags some refusal reasons as sensitive (AML/CFT) without saying which ones, so only
// technical reasons and the identity mismatch are exposed to the end user.
export const TREEZOR_KYC_LIVENESS_USER_REASON_CODES = [
  "1201",
  "1301",
  "1302",
  "1303",
  "1304",
  "1305",
  "1310",
  "1311",
  "1312",
  "1313",
  "1320",
  "1901",
  "1911",
  "2101",
  "2102",
  "2103",
  "2201",
  "2202",
  "2401",
] as const;
export type TreezorKycLivenessUserReasonCode =
  (typeof TREEZOR_KYC_LIVENESS_USER_REASON_CODES)[number];
